import React, { useEffect, useRef, useState } from "react";
import generateUUID from "../utility/generateUUID";

interface Tab {
    label: string;
}

interface TabsProps {
    tabs: Tab[];
    onTabChange?: (active: Tab) => void;
    defaultActiveTab?: number;
}

const Tabs: React.FC<TabsProps> = ({ tabs, onTabChange, defaultActiveTab = 0 }) => {
    const [activeTab, setActiveTab] = useState<number>(defaultActiveTab);
    const containerRef = useRef<HTMLDivElement>(null);
    const gliderRef = useRef<HTMLSpanElement>(null);
    const tabRefs = useRef<(HTMLLabelElement | null)[]>([]);

    const uniqueId = generateUUID();

    const updateGliderPosition = () => {
        const container = containerRef.current;
        const glider = gliderRef.current;
        const activeTabEl = tabRefs.current[activeTab];

        if (!container || !glider || !activeTabEl) return;

        const containerRect = container.getBoundingClientRect();
        const tabRect = activeTabEl.getBoundingClientRect();

        const offsetLeft = tabRect.left - containerRect.left;
        glider.style.transform = `translateX(${offsetLeft}px)`;
        glider.style.width = `${tabRect.width}px`;

        const scrollLeft = offsetLeft - container.clientWidth / 2 + tabRect.width / 2;
        container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    };

    useEffect(() => {
        updateGliderPosition();
        window.addEventListener("resize", updateGliderPosition);

        return () => window.removeEventListener("resize", updateGliderPosition);
    }, [activeTab, tabs]);

    const handleTabChange = (index: number) => {
        setActiveTab(index);

        if (onTabChange) onTabChange(tabs[index]);
    };

    return (
        <div className={`tabs tabs-${uniqueId}`} ref={containerRef}>
            {tabs.map((tab, index) => (
                <React.Fragment key={`${index}-${tab.label}`}>
                    <input
                        type="radio"
                        id={`radio-${index}-${uniqueId}`}
                        name={`tabs-${uniqueId}`}
                        checked={activeTab === index}
                        onChange={() => handleTabChange(index)}
                    />
                    <label className="tab" htmlFor={`radio-${index}-${uniqueId}`} ref={(el) => tabRefs.current[index] = el}>
                        {tab.label}
                    </label>
                </React.Fragment>
            ))}

            <span ref={gliderRef} className={`glider glider-${uniqueId}`}></span>
        </div>
    )
        ;
};

export default Tabs;
